import { ClockIcon } from '@heroicons/react/24/outline'
import { CheckCircleIcon, PlusCircleIcon } from '@heroicons/react/24/solid'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import clsx from 'clsx'
import { useId, useState } from 'react'
import { useLanguageContext } from '../../../context'
import ChallengeInfoModal from '../../challenge-info-modal'
import Badge from '../../tailwind/Badge'
import Button from '../../tailwind/Button'

const I18N_PREFIX = 'create_career.challenge_card.'

/**
 * Eine Challenge als Karte, in zwei Modi.
 *
 * Ohne onClick ist sie reine Anzeige, das sind die Pflicht-Challenges, die
 * niemand abwaehlen kann. Mit onClick ist sie ein Schalter.
 *
 * Der Schalter liegt auf einem eigenen Button, der als Overlay ueber der
 * ganzen Karte sitzt, statt die Karte selbst zum Button zu machen. In der
 * Karte steckt mit "Details" ein zweiter Button, und ein Button in einem
 * Button ist ungueltiges HTML. Vorher war die Karte ein div mit onClick, also
 * per Tastatur gar nicht bedienbar, und der Details-Button brauchte ein
 * stopPropagation, um den Karten-Klick zu unterdruecken. Beides faellt mit
 * dem Overlay weg: ein Aktivierungspfad, ein echter Button, zwei saubere
 * Tabstopps.
 */
const ChallengeCard = ({
  className = '',
  challenge,
  selected = false,
  onClick = undefined
}) => {
  const { t } = useTranslation()
  const { language } = useLanguageContext()

  const [openInfoModal, setOpenInfoModal] = useState(false)

  const id = useId()
  const titleId = `${id}-title`
  const badgesId = `${id}-badges`
  const durationId = `${id}-duration`

  const title = challenge.title[language]

  return (
    <>
      <div
        className={clsx(
          'group relative flex flex-col rounded-xl border border-slate-200 bg-slate-50 p-6',
          // ring-2 statt ring-1: Hover setzte bisher exakt dieselben Klassen
          // wie der ausgewaehlte Zustand, eine ueberfahrene Karte sah aus wie
          // eine ausgewaehlte.
          onClick && selected && '!border-primaryBlue ring-2 ring-primaryBlue',
          onClick &&
            !selected &&
            'hover:border-primaryBlue hover:ring-1 hover:ring-primaryBlue',
          className
        )}
      >
        {onClick && (
          // role checkbox statt aria-pressed: die Auswahl ist ein Zustand in
          // einer Menge, ueber der ein Zaehler "X von Y ausgewaehlt" steht,
          // keine Aktion.
          //
          // outline statt ring wie sonst im Repo: der Overlay-Button ist
          // transparent, ein ring-offset wuerde einen weissen Halo ueber die
          // Karte legen.
          <button
            type='button'
            role='checkbox'
            aria-checked={selected}
            aria-labelledby={titleId}
            aria-describedby={`${badgesId} ${durationId}`}
            onClick={onClick}
            className='absolute -inset-px rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
          />
        )}

        {/* pointer-events-none, sonst schluckt das Icon den Klick auf das
            Overlay darunter. */}
        {onClick && selected && (
          <CheckCircleIcon
            aria-hidden='true'
            className='pointer-events-none absolute right-4 top-4 size-6 text-primaryBlue'
          />
        )}

        {/* Dauerhaft sichtbar statt invisible plus group-hover. Auf Touch gibt
            es kein Hover, per Tastatur war der Hinweis nie zu sehen, und
            visibility hidden nimmt das Icon aus dem Accessibility-Baum. */}
        {onClick && !selected && (
          <PlusCircleIcon
            aria-hidden='true'
            className='pointer-events-none absolute right-4 top-4 size-6 text-gray-300 group-hover:text-primaryBlue'
          />
        )}

        <div className='flex items-center'>
          {/* Dekorativ. Das Bild steht neben Titel, Dimension und
              Beschreibung, unter sm wird es gar nicht gerendert, und die
              Challenge-Daten haben kein Textfeld dafuer. */}
          <img
            src={challenge.card.image}
            alt=''
            className='hidden h-14 w-14 rounded-full object-contain p-1 sm:inline-block'
            style={{ backgroundColor: challenge.backgroundColor }}
          />
          <div className='sm:ml-4'>
            <h3 id={titleId} className='text-2xl font-bold text-gray-900'>
              {title}
            </h3>
            <div id={badgesId} className='mt-0.5 flex flex-wrap gap-1'>
              <Badge
                color='indigo'
                text={challenge.dimensionSynonym[language]}
              />
              {challenge.standard && (
                <Badge
                  color='purple'
                  text={t(I18N_PREFIX + 'default_challenge_label')}
                />
              )}
              {challenge.recommended && (
                <Badge
                  color='green'
                  text={t(I18N_PREFIX + 'recommended_by_aivy_label')}
                />
              )}
            </div>
          </div>
        </div>
        <div className='mt-4'>
          <span className='block text-sm font-semibold text-gray-900'>
            {t(I18N_PREFIX + 'measuring')}
          </span>
          <span className='mt-0.5 block max-w-xl text-sm text-gray-900'>
            {challenge.measuring[language]}
          </span>
        </div>
        <div className='mt-auto' />
        <div className='mt-4 flex items-center'>
          <div id={durationId} className='ml-auto flex items-center'>
            <ClockIcon aria-hidden='true' className='h-6 w-6 text-gray-900' />
            <span className='sr-only'>
              {t(I18N_PREFIX + 'duration_a11y_label')}
            </span>
            <span className='ml-1 whitespace-nowrap text-sm font-medium text-gray-900'>
              {`${challenge.timeToComplete / 60000} ${t('global.minutes')}`}
            </span>
          </div>
          <div className='ml-4' />
          {/* z-10 hebt den Details-Button ueber das Overlay. Button setzt
              selbst schon position relative. */}
          <Button.SecondaryLG
            className='z-10'
            text={t(I18N_PREFIX + 'details_action')}
            ariaLabel={t(I18N_PREFIX + 'details_action_a11y', { title })}
            onClick={() => setOpenInfoModal(true)}
          />
        </div>
      </div>
      <ChallengeInfoModal
        open={openInfoModal}
        setOpen={setOpenInfoModal}
        challenge={challenge}
      />
    </>
  )
}

ChallengeCard.propTypes = {
  className: PropTypes.string,
  challenge: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  onClick: PropTypes.func
}

export default ChallengeCard
